import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { GET_ALL_BOOST_REQUESTS } from '../graphql/queries';
import { APPROVE_BOOST_REQUEST, REJECT_BOOST_REQUEST } from '../graphql/mutations';
import Layout from '../components/Layout';
import { formatDistanceToNow } from 'date-fns';
import {
  Table,
  Button,
  Card,
  Badge,
  Form,
  Modal,
  Row,
  Col,
  Spinner
} from 'react-bootstrap';
import { CheckLg, XLg, Eye, Lightning } from 'react-bootstrap-icons';

const AdminBoostManagement: React.FC = () => {
  const [selectedStatus, setSelectedStatus] = useState<string>('payment_uploaded');
  const [viewingProof, setViewingProof] = useState<string | null>(null);
  const [rejectionModal, setRejectionModal] = useState<{ open: boolean; requestId: string }>({
    open: false,
    requestId: ''
  });
  const [rejectionReason, setRejectionReason] = useState('');

  const { data, loading, refetch } = useQuery(GET_ALL_BOOST_REQUESTS, {
    variables: { status: selectedStatus },
    fetchPolicy: 'cache-and-network'
  });

  const [approveRequest, { loading: approving }] = useMutation(APPROVE_BOOST_REQUEST, {
    onCompleted: () => {
      refetch();
    }
  });

  const [rejectRequest, { loading: rejecting }] = useMutation(REJECT_BOOST_REQUEST, {
    onCompleted: () => {
      setRejectionModal({ open: false, requestId: '' });
      setRejectionReason('');
      refetch();
    }
  });

  const boostRequests = data?.allBoostRequests || [];

  const handleApprove = async (requestId: string) => {
    if (window.confirm('Are you sure you want to approve this boost request?')) {
      await approveRequest({
        variables: {
          boostRequestId: requestId,
          notes: 'Approved after payment verification'
        }
      });
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      alert('Please provide a rejection reason');
      return;
    }

    await rejectRequest({
      variables: {
        boostRequestId: rejectionModal.requestId,
        reason: rejectionReason
      }
    });
  };

  const getStatusBadge = (status: string) => {
    const bgStyles = {
      pending: 'warning',
      payment_uploaded: 'info',
      approved: 'success',
      rejected: 'danger',
      completed: 'primary'
    };

    const labels = {
      pending: 'Pending',
      payment_uploaded: 'Payment Uploaded',
      approved: 'Approved',
      rejected: 'Rejected',
      completed: 'Completed'
    };

    return (
      <Badge bg={bgStyles[status as keyof typeof bgStyles]}>
        {labels[status as keyof typeof labels]}
      </Badge>
    );
  };

  return (
    <Layout showSidebar={false}>
      <div className="container-fluid py-4">
        <div className="mb-4">
          <h1 className="display-6 fw-bold mb-2">Boost Request Management</h1>
          <p className="text-muted">
            Review and manage boost requests from users
          </p>
        </div>

        {/* Status Filter */}
        <Card className="mb-4">
          <Card.Body>
            <div className="d-flex flex-wrap gap-2">
              {['payment_uploaded', 'pending', 'approved', 'rejected', 'completed'].map((status) => (
                <Button
                  key={status}
                  variant={selectedStatus === status ? 'primary' : 'outline-secondary'}
                  onClick={() => {
                    setSelectedStatus(status);
                    refetch({ status });
                  }}
                >
                  {status.replace('_', ' ').toUpperCase()}
                </Button>
              ))}
            </div>
          </Card.Body>
        </Card>

        {/* Stats */}
        <Row className="g-4 mb-4">
          <Col md={3}>
            <Card>
              <Card.Body>
                <div className="display-6 fw-bold text-primary">
                  {data?.allBoostRequests?.filter((r: any) => r.status === 'payment_uploaded').length || 0}
                </div>
                <div className="small text-muted">Awaiting Review</div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card>
              <Card.Body>
                <div className="display-6 fw-bold text-warning">
                  {data?.allBoostRequests?.filter((r: any) => r.status === 'pending').length || 0}
                </div>
                <div className="small text-muted">Pending Payment</div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card>
              <Card.Body>
                <div className="display-6 fw-bold text-success">
                  {data?.allBoostRequests?.filter((r: any) => r.status === 'completed').length || 0}
                </div>
                <div className="small text-muted">Completed</div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card>
              <Card.Body>
                <div className="display-6 fw-bold text-danger">
                  {data?.allBoostRequests?.filter((r: any) => r.status === 'rejected').length || 0}
                </div>
                <div className="small text-muted">Rejected</div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Requests List */}
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" className="mb-3" />
            <p className="text-muted">Loading requests...</p>
          </div>
        ) : boostRequests.length === 0 ? (
          <Card className="text-center p-5">
            <Card.Body>
              <Lightning className="mb-3" size={64} color="#6c757d" />
              <h3 className="h5 fw-bold mb-2">No Requests Found</h3>
              <p className="text-muted mb-0">
                {selectedStatus === 'all'
                  ? 'There are no boost requests yet.'
                  : `There are no ${selectedStatus.replace('_', ' ')} boost requests.`}
              </p>
            </Card.Body>
          </Card>
        ) : (
          <Card>
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table hover className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Ad Details</th>
                      <th>User</th>
                      <th>Package</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {boostRequests.map((request: any) => (
                      <tr key={request.id}>
                        <td>
                          <div className="d-flex align-items-center">
                            {request.ad.primaryImage && (
                              <img
                                src={request.ad.primaryImage}
                                alt={request.ad.title}
                                className="rounded object-cover me-3"
                                style={{ width: '48px', height: '48px' }}
                              />
                            )}
                            <div>
                              <div className="fw-semibold">
                                {request.ad.title}
                              </div>
                              <div className="small text-muted">
                                {request.durationDays} days
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div>{request.user.displayName}</div>
                          <div className="small text-muted">{request.user.email}</div>
                          {request.user.phone && (
                            <div className="small text-muted">{request.user.phone}</div>
                          )}
                        </td>
                        <td>
                          {request.package ? (
                            <div>{request.package.name}</div>
                          ) : (
                            <div className="text-muted">Custom</div>
                          )}
                          {request.transactionId && (
                            <div className="small text-muted mt-1">
                              TXN: {request.transactionId}
                            </div>
                          )}
                        </td>
                        <td className="fw-semibold">
                          Rs. {request.amount}
                        </td>
                        <td>
                          <div className="d-flex flex-column gap-1">
                            {getStatusBadge(request.status)}
                            {request.boostEndsAt && request.status === 'completed' && (
                              <div className="small text-muted">
                                Until {formatDistanceToNow(new Date(request.boostEndsAt), { addSuffix: true })}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="text-muted">
                          {formatDistanceToNow(new Date(request.requestedAt), { addSuffix: true })}
                        </td>
                        <td>
                          <div className="d-flex gap-2">
                            {request.paymentProofUrl && (
                              <Button
                                variant="link"
                                className="p-0 text-primary"
                                onClick={() => setViewingProof(request.paymentProofUrl)}
                                title="View payment proof"
                              >
                                <Eye size={20} />
                              </Button>
                            )}
                            {request.status === 'payment_uploaded' && (
                              <>
                                <Button
                                  variant="link"
                                  className="p-0 text-success"
                                  onClick={() => handleApprove(request.id)}
                                  disabled={approving}
                                  title="Approve"
                                >
                                  <CheckLg size={20} />
                                </Button>
                                <Button
                                  variant="link"
                                  className="p-0 text-danger"
                                  onClick={() => setRejectionModal({ open: true, requestId: request.id })}
                                  disabled={rejecting}
                                  title="Reject"
                                >
                                  <XLg size={20} />
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        )}

        {/* Payment Proof Modal */}
        <Modal
          show={!!viewingProof}
          onHide={() => setViewingProof(null)}
          size="xl"
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Payment Proof</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <img src={viewingProof || ''} alt="Payment proof" className="img-fluid" />
          </Modal.Body>
        </Modal>

        {/* Rejection Modal */}
        <Modal
          show={rejectionModal.open}
          onHide={() => {
            setRejectionModal({ open: false, requestId: '' });
            setRejectionReason('');
          }}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Reject Boost Request</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group>
              <Form.Label>Reason for rejection</Form.Label>
              <Form.Control
                as="textarea"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Enter reason for rejection..."
                rows={4}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => {
                setRejectionModal({ open: false, requestId: '' });
                setRejectionReason('');
              }}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleReject}
              disabled={rejecting || !rejectionReason.trim()}
            >
              {rejecting ? 'Rejecting...' : 'Reject'}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </Layout>
  );
};

export default AdminBoostManagement;
